import React, { useState, useEffect, useRef } from "react";
import { socket } from "../socket";
import { PlayerTag } from "./PlayerIdentity";
import { t } from "../i18n";
import { useSounds, useCountdownTick } from "../sound";
import { useGameTimer } from "../gameTimer";
import { btPlayer, load, fadeOut, stop } from "../btAudio";

// Blind Test « N'oubliez pas les paroles » : la musique défile en karaoké,
// s'arrête au début d'une ligne, et il faut en retrouver les mots.
//
// Le serveur n'envoie que les lignes d'avant et le début visible de la ligne
// cible ; ses mots cachés n'arrivent qu'à la révélation.
function ParolesGame({ gameId, player }) {
  const [state, setState] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [draft, setDraft] = useState("");
  const [needsClick, setNeedsClick] = useState(false);
  const audioRef = useRef(btPlayer(0));
  const stopAt = useRef(null);                 // fondu à cette position
  const { playPop } = useSounds();
  const myId = player?.playerId;

  useEffect(() => {
    socket.on("plState", setState);
    socket.emit("plGetState", { gameId });
    return () => socket.off("plState");
  }, [gameId]);

  useEffect(() => {
    const iv = setInterval(() => {
      setNow(Date.now());
      const a = audioRef.current;
      if (!a || a.paused) return;
      if (stopAt.current !== null && a.currentTime >= stopAt.current) { stopAt.current = null; fadeOut(a, 500); }
    }, 100);
    return () => clearInterval(iv);
  }, []);
  useEffect(() => () => stop(audioRef.current), []);

  const phase = state?.phase;
  const song = state?.song;
  const reveal = state?.reveal;

  // Écoute : chacun cale le morceau au même instant, depuis le même point, et
  // le coupe au début de la ligne à trouver.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !song || phase !== "listen") return undefined;
    setDraft("");
    load(audio, song.mediaUrl);
    stopAt.current = song.lineSec;
    const go = () => {
      audio.currentTime = song.startSec + Math.max(0, (Date.now() - song.startedAt) / 1000);
      audio.play().then(() => setNeedsClick(false)).catch(() => setNeedsClick(true));
    };
    const wait = setTimeout(go, Math.max(0, song.startedAt - Date.now()));
    return () => clearTimeout(wait);
  }, [song?.startedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  // À la réponse, plus de musique ; à la révélation, elle reprend sur la ligne.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (phase === "answer") { stopAt.current = null; fadeOut(audio, 400); }
    if (phase === "reveal" && reveal) {
      playPop();
      audio.currentTime = reveal.resumeSec;
      stopAt.current = reveal.untilSec ?? reveal.resumeSec + 6;
      audio.play().catch(() => {});
    }
  }, [phase]); // eslint-disable-line react-hooks/exhaustive-deps

  // Son bloqué par le navigateur : le prochain toucher le relance.
  useEffect(() => {
    if (!needsClick) return undefined;
    const retry = () => audioRef.current?.play().then(() => setNeedsClick(false)).catch(() => {});
    window.addEventListener("pointerdown", retry);
    window.addEventListener("keydown", retry);
    return () => { window.removeEventListener("pointerdown", retry); window.removeEventListener("keydown", retry); };
  }, [needsClick]);

  const left = state?.endsAt ? Math.max(0, Math.ceil((state.endsAt - now) / 1000)) : 0;
  const singer = state?.turn === "chacun" ? state.players.find(p => p.playerId === state.singerId) : null;
  const iAnswer = !!state && (state.turn !== "chacun" || state.singerId === myId);
  useGameTimer(state ? { remaining: left, duration: state.duration, idle: phase !== "answer" } : null);
  useCountdownTick(left, { active: phase === "answer" && iAnswer });

  if (!state || !song) return <div className="Paroles"><p className="pi-waiting">{t("paroles.waiting")}</p></div>;

  // Karaoké : la ligne en cours et ses voisines ; au bout, la ligne à trouver.
  // Il suit l'horloge de la partie, comme le son : les paroles défilent même
  // si le son est coupé ou lent à charger.
  const pos = song.startSec + Math.max(0, (now - song.startedAt) / 1000);
  const lines = song.lines;
  let cur = -1;
  lines.forEach((l, i) => { if (l.t <= pos + 0.15) cur = i; });
  const atTarget = phase !== "listen" || pos >= song.lineSec - 0.3;
  const sent = state.myAnswer !== null;

  // Révélation : les mots de quelqu'un, colorés — les miens, ou ceux de
  // celui dont c'était le tour.
  const shown = reveal?.results.find(r => r.playerId === myId) || reveal?.results[0];
  const target = reveal ? (
    <p className="pl-target">
      {reveal.words.map((w, i) => {
        const k = i - reveal.blankFrom;
        const cls = k < 0 ? "" : shown?.ok[k] ? " ok" : " ko";
        return <span key={i} className={`pl-word${cls}`}>{w}</span>;
      })}
    </p>
  ) : (
    <p className="pl-target">
      {song.prefix && <span className="pl-word">{song.prefix}</span>}
      {Array.from({ length: song.blanks }, (_, i) => <span key={i} className="pl-blank" />)}
    </p>
  );

  const submit = (e) => {
    e.preventDefault();
    if (phase !== "answer" || !draft.trim()) return;
    socket.emit("plAnswer", { gameId, text: draft });
  };

  return (
    <div className="Paroles">
      <div className="hm-layout">
        <aside className="bt-panel bt-left">
          <ul className="bt-ranking">
            {state.players.map((p, i) => (
              <li key={p.playerId}>
                <PlayerTag player={p} pos={i + 1} score={p.score}
                  className={`${p.playerId === myId ? "me" : ""}${p.offline ? " offline" : ""}${p.playerId === state.singerId ? " hm-giving" : ""}${state.answered.includes(p.playerId) ? " hm-locked" : ""}`} />
              </li>
            ))}
          </ul>
        </aside>

        <main className="pl-main">
          <p className="hm-kicker">
            {t("paroles.songOf", { n: state.index + 1, total: state.total })}
            {song.theme && <small>{song.theme}</small>}
          </p>
          {reveal && <p className="pl-song">{reveal.title}{reveal.artist ? ` — ${reveal.artist}` : ""}</p>}

          <div className="pl-karaoke">
            {!atTarget && <>
              <p className="pl-line past">{lines[cur - 1]?.text || " "}</p>
              <p className="pl-line now">{lines[cur]?.text || "♪"}</p>
              <p className="pl-line next">{lines[cur + 1]?.text || " "}</p>
            </>}
            {atTarget && <>
              <p className="pl-line past">{lines[lines.length - 1]?.text || " "}</p>
              {target}
            </>}
          </div>

          <p className="hm-status">
            {phase === "listen" ? t("paroles.listen")
              : phase === "answer" ? (iAnswer ? (sent ? t("paroles.sent") : t("paroles.yourTurn")) : t("paroles.singerTurn", { pseudo: singer?.pseudo || "?" }))
              : null}
          </p>

          {iAnswer && phase !== "reveal" && (
            <form className="bt-input-row" onSubmit={submit}>
              <input value={draft} autoComplete="off" maxLength={200}
                placeholder={t("paroles.placeholder")} onChange={e => setDraft(e.target.value)} />
              <button className="btn btn-primary" disabled={phase !== "answer" || !draft.trim()}>{t("paroles.send")}</button>
            </form>
          )}

          {reveal && (
            <ul className="pl-results">
              {reveal.results.map(r => {
                const p = state.players.find(x => x.playerId === r.playerId);
                return (
                  <li key={r.playerId} className={r.full ? "full" : ""}>
                    <b>{p?.pseudo || "?"}</b>
                    <span className="pl-said">{r.text || "—"}</span>
                    <span className="pl-gain">+{r.gain}</span>
                  </li>
                );
              })}
            </ul>
          )}

          {needsClick && (
            <button className="btn bt-unmute" onClick={() => audioRef.current?.play().then(() => setNeedsClick(false)).catch(() => {})}>
              {t("blindtest.enableSound")}
            </button>
          )}
        </main>
      </div>
    </div>
  );
}

export default ParolesGame;
